<template>
  <div v-if="isOpen" class="qr-scanner-overlay">
    <div class="scanner-container">
      <div class="scanner-header">
        <h2>QR-Code scannen</h2>
        <button @click="close" class="close-button">✕</button>
      </div>

      <div class="video-container">
        <video ref="videoElement" class="scanner-video" autoplay playsinline></video>
        <div class="scanner-frame"></div>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div class="scanner-instructions">
        <p>Halte den QR-Code der Spielkarte in den Rahmen</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { BrowserQRCodeReader, IScannerControls } from '@zxing/browser';

interface Props {
  isOpen: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  scanned: [value: string];
  close: [];
}>();

const videoElement = ref<HTMLVideoElement | null>(null);
const error = ref<string | null>(null);

let codeReader: BrowserQRCodeReader | null = null;
let scannerControls: IScannerControls | null = null;

/**
 * Startet den Scanner (ZXing managt Kamera + Stream)
 */
async function startScanning() {
  if (!videoElement.value) return;

  try {
    error.value = null;

    if (!codeReader) {
      codeReader = new BrowserQRCodeReader();
    }

    // Continuous Scan über decodeFromVideoDevice
    scannerControls = await codeReader.decodeFromVideoDevice(
      undefined, // undefined = automatisch geeignete Kamera wählen (i. d. R. Back-Cam)
      videoElement.value,
      (result, err, controls) => {
        // controls stehen auch hier zur Verfügung
        if (controls) {
          scannerControls = controls;
        }

        if (result) {
          const text = result.getText();
          console.log('QR-Code gefunden:', text);
          emit('scanned', text);
          close(); // stopScanning() wird hier aufgerufen
        }

        // Fehler ignorieren, die einfach "kein Code gefunden" bedeuten
        if (err && err.name !== 'NotFoundException') {
          console.error('Scanner-Fehler:', err);
        }
      }
    );
  } catch (err) {
    console.error('Kamera-Fehler:', err);
    error.value = 'Kamera-Zugriff verweigert oder nicht verfügbar';
  }
}

/**
 * Stoppt den Scanner
 */
function stopScanning() {
  if (scannerControls) {
    try {
      scannerControls.stop();
    } catch (e) {
      console.warn('Fehler beim Stoppen des Scanners (ignoriert):', e);
    }
    scannerControls = null;
  }

  // Reader-Instanz verwerfen
  codeReader = null;

  // Video-Element aufräumen
  if (videoElement.value) {
    videoElement.value.srcObject = null;
  }
}

/**
 * Schließt den Scanner
 */
function close() {
  stopScanning();
  emit('close');
}

// Watcher: Scanner starten/stoppen wenn isOpen sich ändert
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      setTimeout(startScanning, 100); // Kurze Verzögerung für DOM-Rendering
    } else {
      stopScanning();
    }
  }
);

onMounted(() => {
  if (props.isOpen) {
    startScanning();
  }
});

onUnmounted(() => {
  stopScanning();
});
</script>


<style scoped lang="scss">
.qr-scanner-overlay {
  @apply fixed inset-0;
  background: rgba(0, 0, 0, 0.95);
  z-index: $z-modal;
  @apply flex items-center justify-center;
  animation: fadeIn 0.3s ease;
}

.scanner-container {
  @apply w-full max-w-lg mx-4;
  @apply flex flex-col gap-4;
}

.scanner-header {
  @apply flex items-center justify-between;
  @apply text-white;
  
  h2 {
    @apply text-2xl font-bold m-0;
  }
}

.close-button {
  @apply w-10 h-10 rounded-full;
  @apply bg-gray-800 text-white text-2xl;
  @apply hover:bg-gray-700;
  @apply transition-colors duration-300;
  @apply flex items-center justify-center;
  border: none;
  cursor: pointer;
}

.video-container {
  @apply relative w-full aspect-square;
  @apply rounded-2xl overflow-hidden;
  @apply bg-black;
}

.scanner-video {
  @apply w-full h-full object-cover;
}

.scanner-frame {
  @apply absolute inset-0;
  @apply m-12;
  border: 3px solid $spotify-green;
  border-radius: $radius-xl;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  animation: scannerPulse 2s infinite;
}

.error-message {
  @apply bg-red-900 bg-opacity-30 text-red-400;
  @apply px-4 py-3 rounded-lg text-center;
  border: 1px solid rgba($error, 0.3);
}

.scanner-instructions {
  @apply text-center text-gray-400;
  
  p {
    @apply m-0 text-sm;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scannerPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}
</style>